import type { ProductHydraItem } from "@verbleif/lib/src/composables/Api/Type/Product/product.interface";
import type { PropertyHydraItem } from "@verbleif/lib/src/composables/Api/Type/Property/property.interface";
import type { PropertyGroupHydraItem } from "@verbleif/lib/src/composables/Api/Type/PropertyGroup/property_group.interface";
import type { WarehouseHydraItem } from "@verbleif/lib/src/composables/Api/Type/Warehouse/warehouse.interface";
import type { Ref } from "vue";
import { createGlobalState } from "@vueuse/core";
import { computed, ref } from "vue";
import { loadItem } from "../Api/loadItem";
import { useAuthStore } from "../AuthStore/useAuthStore";
import { invalidateResource } from "../Query/invalidateResource";
import { resourceKeys } from "../Query/resourceKeys";

/**
 * Default and resolution helpers over the warehouses of one location: the selected one for
 * `useWarehouses`, the location of the edited entity for `useEntityWarehouses`.
 */
export function createWarehouseHelpers(
  warehouses: Readonly<Ref<WarehouseHydraItem[]>>,
  multipleWarehousesEnabled: Readonly<Ref<boolean>>,
) {
  const propertyWarehouseCache = new Map<string, string | null>();
  const groupWarehouseCache = new Map<string, string | null>();
  const productWarehouseCache = new Map<string, string | null>();

  const activeWarehouses = computed(() => warehouses.value.filter(warehouse => warehouse.active));
  const hasMultipleWarehouses = computed(() => multipleWarehousesEnabled.value && activeWarehouses.value.length > 1);
  const defaultWarehouse = computed(() =>
    warehouses.value.find(warehouse => warehouse.isDefault)
    ?? activeWarehouses.value[0]
    ?? null,
  );

  function clearCaches() {
    propertyWarehouseCache.clear();
    groupWarehouseCache.clear();
    productWarehouseCache.clear();
  }

  async function warehouseForGroup(groupIri: string | null | undefined): Promise<string | null> {
    if (!groupIri) {
      return defaultWarehouse.value?.["@id"] ?? null;
    }
    if (groupWarehouseCache.has(groupIri)) {
      return groupWarehouseCache.get(groupIri) ?? defaultWarehouse.value?.["@id"] ?? null;
    }

    try {
      const group = await loadItem(groupIri) as PropertyGroupHydraItem | null;
      if (!group) {
        return defaultWarehouse.value?.["@id"] ?? null;
      }
      const resolved = group.defaultWarehouse ?? defaultWarehouse.value?.["@id"] ?? null;
      groupWarehouseCache.set(groupIri, group.defaultWarehouse ?? null);
      return resolved;
    } catch {
      return defaultWarehouse.value?.["@id"] ?? null;
    }
  }

  async function warehouseForProduct(productIri: string | null | undefined): Promise<string | null> {
    if (!productIri) {
      return null;
    }
    if (productWarehouseCache.has(productIri)) {
      return productWarehouseCache.get(productIri) ?? null;
    }

    try {
      const product = await loadItem(productIri) as ProductHydraItem | null;
      const resolved = product?.defaultWarehouse ?? null;
      productWarehouseCache.set(productIri, resolved);
      return resolved;
    } catch {
      return null;
    }
  }

  async function resolveWarehouse(options: {
    warehouseIri?: string | null
    productIri?: string | null
    propertyIri?: string | null
    groupIri?: string | null
  } = {}): Promise<string | null> {
    if (options.warehouseIri) {
      return options.warehouseIri;
    }

    const productWarehouse = await warehouseForProduct(options.productIri);
    if (productWarehouse) {
      return productWarehouse;
    }

    if (options.propertyIri) {
      if (propertyWarehouseCache.has(options.propertyIri)) {
        const cached = propertyWarehouseCache.get(options.propertyIri);
        if (cached) {
          return cached;
        }
        return warehouseForGroup(options.groupIri);
      }

      try {
        const property = await loadItem(options.propertyIri) as PropertyHydraItem | null;
        if (!property) {
          return warehouseForGroup(options.groupIri);
        }
        propertyWarehouseCache.set(options.propertyIri, property.defaultWarehouse ?? null);
        if (property.defaultWarehouse) {
          return property.defaultWarehouse;
        }
        return warehouseForGroup(property.group ?? options.groupIri);
      } catch {
        return warehouseForGroup(options.groupIri);
      }
    }

    return warehouseForGroup(options.groupIri);
  }

  function warehouseName(iri: string | null | undefined): string | null {
    if (!iri) {
      return defaultWarehouse.value?.name ?? null;
    }
    return warehouses.value.find(warehouse => warehouse["@id"] === iri)?.name ?? null;
  }

  return {
    activeWarehouses,
    hasMultipleWarehouses,
    defaultWarehouse,
    clearCaches,
    resolveWarehouse,
    warehouseName,
  };
}

export const useWarehouses = createGlobalState(() => {
  const warehouses = ref<WarehouseHydraItem[]>([]);
  const loading = ref(false);
  // Global state: it reads the selected location's preferences, never those of a dialog that created it.
  const { userPreferences } = useAuthStore();
  const multipleWarehousesEnabled = computed(() => userPreferences.value?.multipleWarehousesEnabled === true);
  const { clearCaches, ...helpers } = createWarehouseHelpers(warehouses, multipleWarehousesEnabled);

  async function load() {
    clearCaches();
    await invalidateResource(resourceKeys.warehouses);
  }

  return {
    warehouses,
    loading,
    reload: load,
    ...helpers,
  };
});
